import { createElement, Square, Circle } from 'lucide';
import JSZip from 'jszip';
import type { AudioEngine } from '../audio/engine';
import type { UnifiedGridVisualizer } from '../ui/visualizer';
import type { RecordingTarget } from '../audio/recorder';
import { updatePlayIcon } from '../ui/icons';
import { updateStatus } from '../ui/status';
import { encodeWav } from '../utils/audio-utils';

/** Note names for MIDI to key conversion */
const NOTE_NAMES = ['C', 'Cs', 'D', 'Ds', 'E', 'F', 'Fs', 'G', 'Gs', 'A', 'As', 'B'];

/** Convert MIDI note to key name */
function midiToKey(midiNote: number): string {
  return NOTE_NAMES[midiNote % 12];
}

/**
 * Format recording time as MM:SS
 */
export function formatRecordingTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Update recording UI state
 */
export function updateRecordingUI(
  isRecording: boolean,
  recordingStartTime: number,
  recordedBlobs: Map<RecordingTarget, Blob> | null
): void {
  const indicator = document.getElementById('recording-indicator');
  const timeDisplay = document.getElementById('recording-time');
  const recordBtn = document.getElementById('record-btn');
  const recordBtnText = document.getElementById('record-btn-text');
  const exportBtn = document.getElementById('export-btn');
  const iconRecord = document.getElementById('icon-record');

  if (indicator) {
    indicator.classList.toggle('active', isRecording);
  }

  if (timeDisplay) {
    const duration = isRecording ? (Date.now() - recordingStartTime) / 1000 : 0;
    timeDisplay.textContent = formatRecordingTime(duration);
  }

  if (recordBtn) {
    recordBtn.classList.toggle('recording', isRecording);
  }

  if (recordBtnText) {
    recordBtnText.textContent = isRecording ? 'Stop' : 'Record';
  }

  if (iconRecord) {
    iconRecord.innerHTML = '';
    const iconAttrsSm = { 'stroke-width': 1.5, width: 12, height: 12 };
    iconRecord.appendChild(createElement(isRecording ? Square : Circle, iconAttrsSm));
  }

  if (exportBtn) {
    exportBtn.style.display = recordedBlobs && recordedBlobs.size > 0 ? 'flex' : 'none';
  }
}

/**
 * Toggle recording on/off
 */
export async function toggleRecording(
  audioEngine: AudioEngine,
  unifiedVisualizer: UnifiedGridVisualizer,
  state: {
    isRecording: boolean;
    recordingStartTime: number;
    recordingTimer: number | null;
    recordedBlobs: Map<RecordingTarget, Blob> | null;
  }
): Promise<{
  isRecording: boolean;
  recordingStartTime: number;
  recordingTimer: number | null;
  recordedBlobs: Map<RecordingTarget, Blob> | null;
}> {
  if (!audioEngine.hasBuffer()) {
    updateStatus('Load a sample first', false);
    return state;
  }

  if (state.isRecording) {
    // Stop recording
    const newIsRecording = false;
    if (state.recordingTimer) {
      clearInterval(state.recordingTimer);
    }

    const recordedBlobs = await audioEngine.stopRecording();
    updateRecordingUI(newIsRecording, state.recordingStartTime, recordedBlobs);
    updateStatus('Recording stopped', false);

    // Show export modal
    showExportModal(recordedBlobs, audioEngine);

    return {
      isRecording: newIsRecording,
      recordingStartTime: state.recordingStartTime,
      recordingTimer: null,
      recordedBlobs,
    };
  } else {
    // Start recording - also start playback if not playing
    if (!audioEngine.getIsPlaying()) {
      await audioEngine.start();
      unifiedVisualizer.start();
      updatePlayIcon(true);
    }

    audioEngine.startRecording();
    const newIsRecording = true;
    const recordingStartTime = Date.now();

    // Update timer every 100ms
    const recordingTimer = window.setInterval(() => {
      updateRecordingUI(newIsRecording, recordingStartTime, null);
    }, 100);

    updateRecordingUI(newIsRecording, recordingStartTime, null);
    updateStatus('Recording...', true);

    return {
      isRecording: newIsRecording,
      recordingStartTime,
      recordingTimer,
      recordedBlobs: null,
    };
  }
}

/**
 * Show export recordings modal
 */
export function showExportModal(
  recordedBlobs: Map<RecordingTarget, Blob> | null,
  audioEngine: AudioEngine
): void {
  if (!recordedBlobs || recordedBlobs.size === 0) {
    updateStatus('No recordings to export', false);
    return;
  }

  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.id = 'export-modal';

  const layerLabels: Record<string, string> = {
    main: 'Main Output (with effects)',
    percussion: 'Percussion Layer',
    bass: 'Bass Layer',
    melody: 'Melody Layer',
    pad: 'Pad Layer'
  };

  const recordingsList = Array.from(recordedBlobs.entries()).map(([target, blob]) => {
    const sizeMB = (blob.size / (1024 * 1024)).toFixed(2);
    return `
      <div class="export-item" data-target="${target}">
        <label class="export-checkbox">
          <input type="checkbox" checked data-export-target="${target}" />
          <span class="export-label">${layerLabels[target] || target}</span>
        </label>
        <span class="export-size">${sizeMB} MB</span>
      </div>
    `;
  }).join('');

  modal.innerHTML = `
    <div class="modal export-modal">
      <div class="modal-header">
        <span class="modal-title">Export Recordings</span>
        <button class="modal-close" id="close-export-modal">×</button>
      </div>
      <p class="export-description">Select which recordings to download as WAV files:</p>
      <div class="export-list">
        ${recordingsList}
      </div>
      <div class="export-actions">
        <button class="px-4 py-2 rounded-md text-xs font-medium cursor-pointer transition-all duration-150 bg-transparent text-[var(--color-foreground)] border border-[var(--color-border)] hover:bg-[var(--color-muted)]" id="select-all-exports">Select All</button>
        <button class="px-4 py-2 rounded-md text-xs font-medium cursor-pointer transition-all duration-150 bg-transparent text-[var(--color-foreground)] border border-[var(--color-border)] hover:bg-[var(--color-muted)]" id="select-none-exports">Select None</button>
      </div>
      <input type="text" class="modal-input" id="export-filename" placeholder="genmus-recording" value="genmus-recording" />
      <div class="modal-actions">
        <button class="px-4 py-2 rounded-md text-xs font-medium cursor-pointer transition-all duration-150 bg-transparent text-[var(--color-foreground)] border border-[var(--color-border)] hover:bg-[var(--color-muted)]" id="cancel-export">Cancel</button>
        <button class="px-4 py-2 rounded-md text-xs font-medium cursor-pointer transition-all duration-150 bg-[var(--color-foreground)] text-[var(--color-background)] border-0 hover:opacity-90" id="confirm-export">Download Selected</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  const closeModal = () => modal.remove();

  document.getElementById('close-export-modal')?.addEventListener('click', closeModal);
  document.getElementById('cancel-export')?.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });

  document.getElementById('select-all-exports')?.addEventListener('click', () => {
    modal.querySelectorAll<HTMLInputElement>('[data-export-target]').forEach(cb => cb.checked = true);
  });

  document.getElementById('select-none-exports')?.addEventListener('click', () => {
    modal.querySelectorAll<HTMLInputElement>('[data-export-target]').forEach(cb => cb.checked = false);
  });

  document.getElementById('confirm-export')?.addEventListener('click', async () => {
    const baseName = (document.getElementById('export-filename') as HTMLInputElement).value.trim() || 'genmus-recording';
    const selectedTargets = new Set<string>();

    modal.querySelectorAll<HTMLInputElement>('[data-export-target]:checked').forEach(cb => {
      selectedTargets.add(cb.dataset.exportTarget!);
    });

    if (selectedTargets.size === 0) {
      updateStatus('Select at least one recording', false);
      return;
    }

    closeModal();
    updateStatus('Exporting...', false);

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    const bpm = Math.round(audioEngine.getBPM());
    const key = midiToKey(audioEngine.getRootNote());
    const scale = audioEngine.getScale();

    // Collect all selected recordings and convert to WAV
    const filesToZip: Array<{ name: string; data: Blob }> = [];
    
    for (const [target, blob] of recordedBlobs!) {
      if (selectedTargets.has(target)) {
        const filename = target === 'main'
          ? `${baseName}_${bpm}bpm_${key}-${scale}_${timestamp}_main.wav`
          : `${baseName}_${bpm}bpm_${key}-${scale}_${timestamp}_${target}.wav`;
        const wavBlob = await convertToWav(blob);
        filesToZip.push({ name: filename, data: wavBlob });
      }
    }

    // If only one file, download directly; otherwise create ZIP
    if (filesToZip.length === 1) {
      await downloadFile(filesToZip[0].data, filesToZip[0].name);
      updateStatus('Exported 1 file', false);
    } else {
      const zip = new JSZip();
      for (const file of filesToZip) {
        zip.file(file.name, file.data);
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      await downloadFile(zipBlob, `${baseName}_${bpm}bpm_${key}-${scale}_${timestamp}.zip`);
      updateStatus(`Exported ${filesToZip.length} files as ZIP`, false);
    }

    setTimeout(() => updateStatus(audioEngine.getIsPlaying() ? 'Playing' : 'Ready', audioEngine.getIsPlaying()), 2000);
  });
}

/**
 * Download a blob as a file
 */
async function downloadFile(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Convert webm blob to WAV format
 */
async function convertToWav(webmBlob: Blob): Promise<Blob> {
  try {
    const arrayBuffer = await webmBlob.arrayBuffer();
    const audioContext = new AudioContext();

    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const wavBuffer = encodeWav(audioBuffer);
    await audioContext.close();

    return new Blob([wavBuffer], { type: 'audio/wav' });
  } catch (error) {
    console.warn('Could not convert to WAV, returning original format');
    return webmBlob;
  }
}

/**
 * Export recordings (shows modal)
 */
export function exportRecordings(
  recordedBlobs: Map<RecordingTarget, Blob> | null,
  audioEngine: AudioEngine
): void {
  showExportModal(recordedBlobs, audioEngine);
}
